import { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { MessageSquare, X, Trash2, BellOff, Bell, MoreVertical, Sparkles, Phone, Instagram, AlertTriangle, User, UserPlus, Search, SquarePen, ChevronLeft, ChevronRight, CheckCircle, RotateCcw, FileText, ArrowDownUp, RefreshCw } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { useUserRole } from "@/hooks/useUserRole";
import { ConversationList } from "@/components/conversations/ConversationList";
import { ChatInterface } from "@/components/chat/ChatInterface";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannelMulti } from "@/lib/realtime-channel";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChannelToggle } from "@/components/conversations/ChannelToggle";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { PaywallOverlay } from "@/components/layout/PaywallOverlay";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LeadDetailDrawer } from "@/components/leads/LeadDetailDrawer";
import { InstagramCommentsView } from "@/components/conversations/InstagramCommentsView";
import { Input } from "@/components/ui/input";
import { NewConversationDialog } from "@/components/conversations/NewConversationDialog";
import { IntegrationChoiceDialog } from "@/components/conversations/IntegrationChoiceDialog";
import {
  fetchMemberAccessibleWhatsAppIntegrations,
  type MessagingIntegrationOption,
} from "@/lib/member-accessible-integrations";
import { startLeadConversation } from "@/lib/start-lead-conversation";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MemberAssignPopover, type MemberAssignPopoverHandle } from "@/components/conversations/MemberAssignPopover";
import { LeadTagsPopover } from "@/components/conversations/LeadTagsPopover";
import { ConversationAttendingNotice } from "@/components/conversations/ConversationAttendingNotice";
import { ConversationPhoneSwitch } from "@/components/conversations/ConversationPhoneSwitch";
import { ConversationChatHeaderActions, ConversationChatHeaderExpandPanel } from "@/components/conversations/ConversationChatHeaderActions";
import { ConversationLeadFunnelControls } from "@/components/conversations/ConversationLeadFunnelControls";
import type { Lead } from "@/types/lead";
import { ConversationSummarySheet } from "@/components/conversations/ConversationSummarySheet";
import { LeadRecoverySheet, type LeadRecoveryData } from "@/components/conversations/LeadRecoverySheet";
import { useWorkspacePlan } from "@/hooks/useWorkspacePlan";
import { ScheduleCloseDialog } from "@/components/conversations/ScheduleCloseDialog";
import { ConversationBulkActionsBar } from "@/components/conversations/ConversationBulkActionsBar";
import type { MappedConversation } from "@/lib/conversation-list-map";
import { getPinnedConversationIds } from "@/lib/conversation-pin-storage";
import { useLeadEscalations } from "@/hooks/useLeadEscalations";
import { useLeadTags } from "@/hooks/useLeadTags";
import { LeadTagBadge } from "@/components/leads/LeadTagBadge";
import { isUnassignedIntegrationFilter } from "@/lib/conversation-integration-assign";
import { cn } from "@/lib/utils";
import {
  ATTENTION_FILTER_LABELS,
  defaultAttentionFromTabOrder,
  defaultStatusFilter,
  loadPersistedInboxStatus,
  persistInboxStatus,
  showAllAttentionTab,
  type ConversationAttentionFilter,
  type ConversationFilterCounts,
  type ConversationStatusFilter,
} from "@/lib/conversation-filters";
import { ConversationInboxLayoutSettings } from "@/components/conversations/ConversationInboxLayoutSettings";
import { dispatchConversationLeadPatch } from "@/lib/conversation-lead-patch";
import { dispatchConversationInboxRowPatch } from "@/lib/conversation-inbox-patch";
import { markConversationRead } from "@/lib/conversation-member-read";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { fetchLeadFunnelPlacement } from "@/lib/lead-funnel-placement";
import { applyEscalationResolvedLocally } from "@/lib/resolve-escalation-followup";
import type { ResolveEscalationResult } from "@/lib/resolve-escalation-followup";

import {
  AlertDialog as ResolveAlertDialog,
  AlertDialogAction as ResolveAlertDialogAction,
  AlertDialogCancel as ResolveAlertDialogCancel,
  AlertDialogContent as ResolveAlertDialogContent,
  AlertDialogDescription as ResolveAlertDialogDescription,
  AlertDialogFooter as ResolveAlertDialogFooter,
  AlertDialogHeader as ResolveAlertDialogHeader,
  AlertDialogTitle as ResolveAlertDialogTitle,
  AlertDialogTrigger as ResolveAlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type ConversationChannel = "whatsapp" | "instagram" | "messenger";

function parseConversationChannel(value: string | null): ConversationChannel | null {
  if (value === "whatsapp" || value === "instagram" || value === "messenger") return value;
  return null;
}

/**
 * Inline button rendered in the chat header that lets the operator mark all
 * pending escalations for the current lead as resolved. Visible only when
 * pendingCount > 0.
 */
function ResolveEscalationButton({
  leadId,
  conversationId,
  workspaceId,
  memberId,
  integrationId,
  onResolved,
  className,
  showLabel = false,
}: {
  leadId: string | null | undefined;
  conversationId?: string | null;
  workspaceId?: string | null;
  memberId?: string | null;
  integrationId?: string | null;
  onResolved?: (result: ResolveEscalationResult) => void | Promise<void>;
  className?: string;
  showLabel?: boolean;
}) {
  const { pendingCount, resolveAll } = useLeadEscalations(leadId);
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [reactivateAgent, setReactivateAgent] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!leadId || pendingCount <= 0) return null;

  const handleResolve = async () => {
    setSubmitting(true);
    const result = await resolveAll({
      reactivateAgent,
      conversationId,
      memberId,
      workspaceId,
      integrationId,
    });
    setSubmitting(false);
    if (result.ok) {
      let description = `${result.resolved} ${result.resolved === 1 ? "evento marcado" : "eventos marcados"} como resolvido.`;
      if (result.stageRestored) {
        description += " Lead devolvido à coluna anterior.";
      }
      if (result.agentReactivated) {
        description += " Agente de IA reativado.";
      } else if (result.agentBlocked) {
        description += " IA não reativada: estágio bloqueia o agente.";
      } else if (result.agentError) {
        description += ` IA não reativada: ${result.agentError}`;
      }
      toast({
        title: "Escalação resolvida",
        description,
      });
      setDialogOpen(false);
      setReactivateAgent(true);
      if (onResolved) {
        await onResolved(result);
      }
    } else {
      toast({
        title: "Erro ao resolver",
        description: result.error || "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <ResolveAlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <ResolveAlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size={showLabel ? "sm" : "icon"}
                className={cn(
                  "relative shrink-0 text-amber-600 hover:bg-amber-500/10 hover:text-amber-700 dark:text-amber-400 dark:hover:bg-amber-500/10 dark:hover:text-amber-300",
                  showLabel ? "h-7 gap-1 px-2 text-xs sm:h-8" : "h-7 w-7 sm:h-8 sm:w-8",
                  className,
                )}
              >
                <AlertTriangle className="size-4 motion-safe:animate-pulse" aria-hidden />
                {showLabel ? (
                  <span>Resolver{pendingCount > 1 ? ` (${pendingCount})` : ""}</span>
                ) : (
                  <span className="sr-only">
                    Resolver escalação{pendingCount > 1 ? ` (${pendingCount} pendentes)` : ""}
                  </span>
                )}
                {!showLabel && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-amber-500 px-0.5 text-[9px] font-bold leading-none text-white tabular-nums">
                    {pendingCount}
                  </span>
                )}
              </Button>
            </ResolveAlertDialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {pendingCount === 1
              ? "1 escalação pendente — clique para resolver"
              : `${pendingCount} escalações pendentes — clique para resolver`}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <ResolveAlertDialogContent>
        <ResolveAlertDialogHeader>
          <ResolveAlertDialogTitle>Marcar escalações como resolvidas?</ResolveAlertDialogTitle>
          <ResolveAlertDialogDescription>
            Isso vai marcar {pendingCount === 1 ? "a escalação pendente" : `as ${pendingCount} escalações pendentes`} deste lead como resolvido.
            O histórico continua disponível na ficha do lead.
          </ResolveAlertDialogDescription>
          {conversationId ? (
            <div className="flex items-start gap-2 rounded-md border p-3">
              <Checkbox
                id="reactivate-agent-after-resolve"
                checked={reactivateAgent}
                onCheckedChange={(checked) => setReactivateAgent(checked === true)}
                disabled={submitting}
              />
              <div className="space-y-1">
                <Label htmlFor="reactivate-agent-after-resolve" className="text-sm font-medium leading-none">
                  Reativar agente de IA
                </Label>
                <p className="text-xs text-muted-foreground">
                  Retoma respostas automáticas nesta conversa após resolver a escalação.
                </p>
              </div>
            </div>
          ) : null}
        </ResolveAlertDialogHeader>
        <ResolveAlertDialogFooter>
          <ResolveAlertDialogCancel>Cancelar</ResolveAlertDialogCancel>
          <ResolveAlertDialogAction onClick={handleResolve} disabled={submitting}>
            {submitting ? "Resolvendo..." : "Resolver"}
          </ResolveAlertDialogAction>
        </ResolveAlertDialogFooter>
      </ResolveAlertDialogContent>
    </ResolveAlertDialog>
  );
}
interface SelectedConversation {
  conversation_id: string;
  integration_id?: string | null;
  channel: string;
  lead_id: string;
  lead_name: string;
  lead_phone: string | null;
  state: string;
  followup_enabled: boolean;
  metadata?: any;
  is_closed?: boolean;
  originLabel?: string | null;
  assigned_member_id?: string | null;
  assigned_member_ids?: string[];
  lead_stage?: string;
  lead_funnel_id?: string | null;
  stage_auto_disables_agent?: boolean;
  isUrgent?: boolean;
}

const AGENT_BLOCKED_RESUME_TOAST = {
  title: "IA bloqueada neste estágio",
  description:
    "O lead está em um estágio configurado para desligar a IA. Mova o lead para outro estágio antes de reativar.",
  variant: "destructive" as const,
};

/** Largura mínima (px) do cabeçalho do chat para exibir todas as ações inline. */
const CHAT_HEADER_WIDE_MIN_PX = 1280;

function ConversationInboxFilters({
  attentionFilter,
  setAttentionFilter,
  statusFilter,
  setStatusFilter,
  filterCounts,
  tabOrder,
  onSaveTabOrder,
}: {
  attentionFilter: ConversationAttentionFilter;
  setAttentionFilter: (v: ConversationAttentionFilter) => void;
  statusFilter: ConversationStatusFilter;
  setStatusFilter: (v: ConversationStatusFilter) => void;
  filterCounts: ConversationFilterCounts;
  tabOrder: ConversationAttentionFilter[];
  onSaveTabOrder: (order: ConversationAttentionFilter[]) => Promise<boolean>;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll, tabOrder]);

  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -150 : 150, behavior: "smooth" });
  };

  const tabClass = (active: boolean) =>
    cn(
      "inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap shrink-0 transition-colors cursor-pointer",
      active
        ? "bg-background shadow-sm text-foreground"
        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
    );

  const countByTab: Record<ConversationAttentionFilter, number> = {
    mine: filterCounts.mine,
    attention: filterCounts.attention,
    ia: filterCounts.ia,
    all: filterCounts.all,
  };

  const attentionTabs = tabOrder.map((value) => ({
    value,
    label: ATTENTION_FILTER_LABELS[value].label,
    title: ATTENTION_FILTER_LABELS[value].title,
    badgeClass: ATTENTION_FILTER_LABELS[value].badgeClass,
    count: countByTab[value],
  }));

  const statusOptions: { value: ConversationStatusFilter; label: string; count: number }[] = [
    { value: "open", label: "Abertas", count: filterCounts.open },
    { value: "closed", label: "Encerradas", count: filterCounts.closed },
  ];

  return (
    <div className="flex items-center gap-1 w-full">
      <ConversationInboxLayoutSettings tabOrder={tabOrder} onSave={onSaveTabOrder} />
      {showLeft && (
        <button
          type="button"
          onClick={() => scroll("left")}
          className="h-6 w-6 shrink-0 flex items-center justify-center rounded-full bg-muted/50 hover:bg-muted transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </button>
      )}
      <div
        ref={scrollRef}
        className="flex flex-1 min-w-0 overflow-x-auto scrollbar-hide items-center gap-1 px-1"
      >
        {attentionTabs.map((f) => (
          <button
            type="button"
            key={f.value}
            title={f.title}
            onClick={() => setAttentionFilter(f.value)}
            className={tabClass(attentionFilter === f.value)}
          >
            {f.label}
            {f.count > 0 && (
              <Badge
                variant="secondary"
                className={cn(
                  "ml-1 h-5 min-w-5 px-1.5 text-[10px] font-medium rounded-full",
                  f.badgeClass
                )}
              >
                {f.count}
              </Badge>
            )}
          </button>
        ))}

        <span className="mx-1 h-4 w-px shrink-0 bg-border" aria-hidden />

        <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0 px-0.5">
          Status
        </span>
        {statusOptions.map((s) => (
          <button
            type="button"
            key={s.value}
            title={s.value === "open" ? "Somente conversas abertas" : "Somente conversas encerradas"}
            onClick={() => setStatusFilter(s.value)}
            className={tabClass(statusFilter === s.value)}
          >
            {s.label}
            {s.count > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-[10px] font-medium rounded-full">
                {s.count}
              </Badge>
            )}
          </button>
        ))}
      </div>
      {showRight && (
        <button
          type="button"
          onClick={() => scroll("right")}
          className="h-6 w-6 shrink-0 flex items-center justify-center rounded-full bg-muted/50 hover:bg-muted transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}

function SortAndFilterMenu({
  prioritizeUnread,
  setPrioritizeUnread,
  unreadCount,
  workspaceTags,
  selectedTagIds,
  setSelectedTagIds,
  filterUpcomingAppointments,
  setFilterUpcomingAppointments,
  filterCancelledAppointments,
  setFilterCancelledAppointments,
  filterUnreadOnly,
  setFilterUnreadOnly,
}: {
  prioritizeUnread: boolean;
  setPrioritizeUnread: (value: boolean) => void;
  unreadCount: number;
  workspaceTags: { id: string; name: string; color: string }[];
  selectedTagIds: string[];
  setSelectedTagIds: (ids: string[]) => void;
  filterUpcomingAppointments: boolean;
  setFilterUpcomingAppointments: (value: boolean) => void;
  filterCancelledAppointments: boolean;
  setFilterCancelledAppointments: (value: boolean) => void;
  filterUnreadOnly: boolean;
  setFilterUnreadOnly: (value: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'root' | 'tags'>('root');
  const hasTagFilter = selectedTagIds.length > 0;
  const appointmentFilterCount =
    (filterUpcomingAppointments ? 1 : 0) + (filterCancelledAppointments ? 1 : 0);
  const inboxFilterCount =
    (filterUnreadOnly ? 1 : 0) + appointmentFilterCount;
  const hasInboxFilter = inboxFilterCount > 0;
  const hasActiveFilter = prioritizeUnread || hasTagFilter || hasInboxFilter;

  const toggleTag = (id: string) => {
    if (selectedTagIds.includes(id)) {
      setSelectedTagIds(selectedTagIds.filter((t) => t !== id));
    } else {
      setSelectedTagIds([...selectedTagIds, id]);
    }
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setView('root');
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant={hasActiveFilter ? "default" : "ghost"}
          size="icon"
          className="h-9 w-9 shrink-0 relative"
          title="Ordenar e filtrar"
        >
          <ArrowDownUp className="w-5 h-5" />
          {prioritizeUnread && unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-4 h-4 rounded-full bg-green-500 px-1 text-[9px] font-bold leading-4 text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
          {!prioritizeUnread && hasTagFilter && (
            <span className="absolute -top-1 -right-1 min-w-4 h-4 rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground">
              {selectedTagIds.length}
            </span>
          )}
          {!prioritizeUnread && !hasTagFilter && hasInboxFilter && (
            <span className="absolute -top-1 -right-1 min-w-4 h-4 rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground">
              {inboxFilterCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {view === 'root' ? (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">Ordenar</DropdownMenuLabel>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setPrioritizeUnread(!prioritizeUnread);
              }}
              className="flex items-center justify-between"
            >
              <span>Não vistas primeiro</span>
              {prioritizeUnread && <CheckCircle className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Filtrar</DropdownMenuLabel>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setFilterUnreadOnly(!filterUnreadOnly);
              }}
              className="flex items-center justify-between"
            >
              <span>Somente não lidas</span>
              {filterUnreadOnly && <CheckCircle className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setFilterUpcomingAppointments(!filterUpcomingAppointments);
              }}
              className="flex items-center justify-between"
            >
              <span>Agendamentos futuros</span>
              {filterUpcomingAppointments && <CheckCircle className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setFilterCancelledAppointments(!filterCancelledAppointments);
              }}
              className="flex items-center justify-between"
            >
              <span>Com cancelamento</span>
              {filterCancelledAppointments && <CheckCircle className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setView('tags');
              }}
              className="flex items-center justify-between"
            >
              <span>Por tag</span>
              {hasTagFilter && (
                <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px]">
                  {selectedTagIds.length}
                </Badge>
              )}
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between px-2 py-1.5">
              <button
                type="button"
                onClick={() => setView('root')}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="w-3 h-3" />
                Voltar
              </button>
              {hasTagFilter && (
                <button
                  type="button"
                  onClick={() => setSelectedTagIds([])}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Limpar
                </button>
              )}
            </div>
            <DropdownMenuSeparator />
            <div className="max-h-64 overflow-y-auto">
              {workspaceTags.length === 0 ? (
                <p className="px-2 py-3 text-xs text-muted-foreground text-center">
                  Nenhuma tag cadastrada
                </p>
              ) : (
                workspaceTags.map((tag) => {
                  const isSelected = selectedTagIds.includes(tag.id);
                  return (
                    <DropdownMenuItem
                      key={tag.id}
                      onSelect={(e) => {
                        e.preventDefault();
                        toggleTag(tag.id);
                      }}
                      className="flex items-center gap-2"
                    >
                      <div className={`w-3 h-3 rounded-full ${tag.color}`} />
                      <span className="flex-1 truncate">{tag.name}</span>
                      {isSelected && <CheckCircle className="w-4 h-4 text-primary" />}
                    </DropdownMenuItem>
                  );
                })
              )}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


type InstagramView = "inbox" | "comments";

function InstagramViewTabs({
  value,
  onChange,
  className,
}: {
  value: InstagramView;
  onChange: (v: InstagramView) => void;
  className?: string;
}) {
  return (
    <Tabs
      value={value}
      onValueChange={(v) => onChange(v as InstagramView)}
      className={cn("shrink-0", className)}
    >
      <TabsList>
        <TabsTrigger value="inbox" className="px-3 text-xs">
          Conversas
        </TabsTrigger>
        <TabsTrigger value="comments" className="px-3 text-xs">
          Comentários
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
export default function Conversations() {
  const {
    user
  } = useAuth();
  const {
    workspaceId,
    loading: workspaceLoading
  } = useWorkspace(user?.id);
  const { plusPlanAllowed } = useWorkspacePlan(workspaceId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedChannel, setSelectedChannel] = useState<ConversationChannel>(
    () => parseConversationChannel(searchParams.get("channel")) || "whatsapp",
  );
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(
    () => searchParams.get("integration_id"),
  );
  const [selectedConversation, setSelectedConversation] = useState<SelectedConversation | null>(null);
  const [scheduleCloseOpen, setScheduleCloseOpen] = useState(false);
  const [instagramView, setInstagramView] = useState<'inbox' | 'comments'>('inbox');
  const [searchTerm, setSearchTerm] = useState("");
  const mobileAssignRef = useRef<MemberAssignPopoverHandle | null>(null);
  const chatHeaderMeasureRef = useRef<HTMLDivElement>(null);
  const chatHeaderActionsRef = useRef<HTMLDivElement>(null);
  const [useWideChatHeader, setUseWideChatHeader] = useState(false);
  const [leadActionsExpanded, setLeadActionsExpanded] = useState(false);
  const [attentionFilter, setAttentionFilter] = useState<ConversationAttentionFilter>("mine");
  const [statusFilter, setStatusFilter] = useState<ConversationStatusFilter>("open");
  const [prioritizeUnread, setPrioritizeUnread] = useState(false);
  const [filterUpcomingAppointments, setFilterUpcomingAppointments] = useState(false);
  const [filterCancelledAppointments, setFilterCancelledAppointments] = useState(false);
  const [filterUnreadOnly, setFilterUnreadOnly] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const { workspaceTags } = useLeadTags({ workspaceId: workspaceId || "" });

  const [filterCounts, setFilterCounts] = useState<ConversationFilterCounts>({
    mine: 0,
    attention: 0,
    ia: 0,
    all: 0,
    open: 0,
    closed: 0,
    unread: 0,
  });
  const {
    toast
  } = useToast();
  const {
    shouldShowPaywall
  } = useSubscription();
  const {
    memberId,
    memberDisplayName,
    isAdmin,
    conversationsVisibility,
    inboxTabOrder,
    saveInboxTabOrder,
  } = useMemberPermissions();

  const showAllTab = showAllAttentionTab(conversationsVisibility, isAdmin);

  useEffect(() => {
    if (!workspaceId || inboxTabOrder.length === 0) return;
    setAttentionFilter(defaultAttentionFromTabOrder(inboxTabOrder));
    const persistedStatus = loadPersistedInboxStatus(workspaceId);
    setStatusFilter(persistedStatus ?? defaultStatusFilter());
  }, [workspaceId, inboxTabOrder]);

  useEffect(() => {
    if (!workspaceId) return;
    persistInboxStatus(workspaceId, statusFilter);
  }, [workspaceId, statusFilter]);

  useEffect(() => {
    if (!inboxTabOrder.includes(attentionFilter)) {
      setAttentionFilter(defaultAttentionFromTabOrder(inboxTabOrder));
    }
  }, [inboxTabOrder, attentionFilter]);

  const handleSaveTabOrder = useCallback(
    async (order: ConversationAttentionFilter[]) => {
      const ok = await saveInboxTabOrder(order);
      if (ok) {
        setAttentionFilter(defaultAttentionFromTabOrder(order));
        toast({
          title: "Preferência salva",
          description: `A aba "${ATTENTION_FILTER_LABELS[order[0]].label}" abrirá ao entrar em Conversas.`,
        });
      } else {
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar sua preferência. Tente novamente.",
          variant: "destructive",
        });
      }
      return ok;
    },
    [saveInboxTabOrder, toast]
  );
  const isMobile = useIsMobile();
  const mobileShellClassName = "h-full min-h-0 w-full flex flex-col overflow-hidden";
  const handleChannelChange = useCallback((value: ConversationChannel) => {
    setSelectedChannel(value);
    setSelectedIntegrationId(null);
    setSelectedConversation(null);
    if (value === "instagram") setInstagramView("inbox");
  }, []);
  const {
    isOwner
  } = useUserRole();

  useLayoutEffect(() => {
    const header = chatHeaderMeasureRef.current;
    if (!header) return;
    const measure = () => {
      const width = header.getBoundingClientRect().width;
      setUseWideChatHeader(width >= CHAT_HEADER_WIDE_MIN_PX);
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(header);
    return () => ro.disconnect();
  }, [selectedConversation?.conversation_id]);

  useEffect(() => {
    setLeadActionsExpanded(false);
  }, [selectedConversation?.conversation_id]);

  useEffect(() => {
    if (useWideChatHeader) setLeadActionsExpanded(false);
  }, [useWideChatHeader]);

  useEffect(() => {
    const channelFromUrl = parseConversationChannel(searchParams.get("channel"));
    const integrationFromUrl = searchParams.get("integration_id");

    // Só sincroniza filtros presentes na URL — ausência de param não significa "limpar"
    // (ex.: deep link via conversation_id que depois remove os query params).
    if (searchParams.has("channel") && channelFromUrl) {
      setSelectedChannel((current) => {
        if (current === channelFromUrl) return current;
        setSelectedConversation(null);
        if (channelFromUrl === "instagram") setInstagramView("inbox");
        return channelFromUrl;
      });
    }

    if (searchParams.has("integration_id")) {
      setSelectedIntegrationId((current) => {
        if (current === integrationFromUrl) return current;
        setSelectedConversation(null);
        return integrationFromUrl;
      });
    }
  }, [searchParams]);

  // Se a URL aponta para integração removida, limpar filtro (conversas continuam no canal / em aberto).
  useEffect(() => {
    if (!workspaceId || !selectedIntegrationId) return;
    if (isUnassignedIntegrationFilter(selectedIntegrationId)) return;

    void (async () => {
      const { data } = await supabase
        .from("integrations")
        .select("id")
        .eq("id", selectedIntegrationId)
        .eq("workspace_id", workspaceId)
        .maybeSingle();

      if (!data) {
        setSelectedIntegrationId(null);
      }
    })();
  }, [workspaceId, selectedIntegrationId]);

  // Se a conversa selecionada deixar de ser visível para o membro (foi desatribuído), desmarcar.
  useEffect(() => {
    if (isAdmin) return;
    if (!selectedConversation || !workspaceId || !memberId) return;
    const leadId = selectedConversation.lead_id;
    if (!leadId) return;

    const checkStillVisible = async () => {
      const { data } = await supabase
        .from("lead_member_assignments")
        .select("member_id")
        .eq("lead_id", leadId);

      const assigneeIds = (data ?? []).map((row) => row.member_id);
      const stillVisible =
        assigneeIds.length === 0 || assigneeIds.includes(memberId);

      if (!stillVisible) {
        setSelectedConversation(null);
        toast({
          title: "Conversa reatribuída",
          description: "Esta conversa foi atribuída a outro membro da equipe.",
        });
      }
    };

    const channelName = `lead-assignment-watch-${leadId}`;
    const channel = subscribePostgresChannelMulti(channelName, [
      {
        binding: {
          event: "*",
          schema: "public",
          table: "lead_member_assignments",
          filter: `lead_id=eq.${leadId}`,
        },
        callback: () => {
          void checkStillVisible();
        },
      },
      {
        binding: {
          event: "UPDATE",
          schema: "public",
          table: "leads",
          filter: `id=eq.${leadId}`,
        },
        callback: () => {
          void checkStillVisible();
        },
      },
    ]);

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [selectedConversation, workspaceId, memberId, isAdmin, toast]);

  // Admin: mantém aviso de atendimento sincronizado quando a atribuição do lead muda.
  useEffect(() => {
    if (!isAdmin || !selectedConversation?.lead_id || !workspaceId) return;
    const leadId = selectedConversation.lead_id;

    const refreshAssignees = async () => {
      const { data } = await supabase
        .from("lead_member_assignments")
        .select("member_id")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: true });

      const memberIds = (data ?? []).map((row) => row.member_id);
      const primaryId = memberIds[0] ?? null;

      setSelectedConversation((prev) => {
        if (!prev || prev.lead_id !== leadId) return prev;
        return {
          ...prev,
          assigned_member_id: primaryId,
          assigned_member_ids: memberIds,
        };
      });
      dispatchConversationLeadPatch({
        leadId,
        assignedMemberId: primaryId,
        assignedMemberIds: memberIds,
      });
    };

    const channelName = `lead-assignment-admin-${leadId}`;
    const channel = subscribePostgresChannelMulti(channelName, [
      {
        binding: {
          event: "*",
          schema: "public",
          table: "lead_member_assignments",
          filter: `lead_id=eq.${leadId}`,
        },
        callback: () => {
          void refreshAssignees();
        },
      },
      {
        binding: {
          event: "UPDATE",
          schema: "public",
          table: "leads",
          filter: `id=eq.${leadId}`,
        },
        callback: (payload) => {
          const newAssigned =
            (payload.new as { assigned_member_id?: string | null })?.assigned_member_id ??
            null;
          setSelectedConversation((prev) => {
            if (!prev || prev.lead_id !== leadId) return prev;
            return { ...prev, assigned_member_id: newAssigned };
          });
        },
      },
    ]);

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [isAdmin, selectedConversation?.lead_id, workspaceId]);

  const handleLeadMemberAssign = useCallback(
    (memberIds: string[], memberLabels?: string[]) => {
      const primaryId = memberIds[0] ?? null;
      const label = memberLabels?.length ? memberLabels.join(", ") : null;
      if (selectedConversation?.lead_id) {
        dispatchConversationLeadPatch({
          leadId: selectedConversation.lead_id,
          assignedMemberId: primaryId,
          assignedMemberIds: memberIds,
          assignedMemberLabel: label,
        });
      }
      setSelectedConversation((prev) =>
        prev
          ? {
              ...prev,
              assigned_member_id: primaryId,
              assigned_member_ids: memberIds,
            }
          : prev,
      );
    },
    [selectedConversation?.lead_id],
  );

  const [clearContextDialog, setClearContextDialog] = useState<{
    open: boolean;
    conversationId: string | null;
  }>({
    open: false,
    conversationId: null
  });
  const [deleteLeadDialog, setDeleteLeadDialog] = useState<{
    open: boolean;
    leadId: string | null;
    leadName: string | null;
  }>({
    open: false,
    leadId: null,
    leadName: null
  });
  const [conversationsRefreshKey, setConversationsRefreshKey] = useState(0);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedConversationIds, setSelectedConversationIds] = useState<Set<string>>(new Set());
  const [selectedConversationsForBulk, setSelectedConversationsForBulk] = useState<MappedConversation[]>([]);
  const [pinnedConversationIds, setPinnedConversationIds] = useState<Set<string>>(() =>
    workspaceId ? getPinnedConversationIds(workspaceId) : new Set(),
  );
  const [visibleSelectableIds, setVisibleSelectableIds] = useState<string[]>([]);
  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [fullLead, setFullLead] = useState<Lead | null>(null);
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [newLeadIntegrationChoiceOpen, setNewLeadIntegrationChoiceOpen] = useState(false);
  const [newLeadIntegrationChoices, setNewLeadIntegrationChoices] = useState<MessagingIntegrationOption[]>([]);
  const [pendingNewLeadId, setPendingNewLeadId] = useState<string | null>(null);
  const [isStartingNewLeadConversation, setIsStartingNewLeadConversation] = useState(false);
  const [showSummarySheet, setShowSummarySheet] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [showLeadRecoverySheet, setShowLeadRecoverySheet] = useState(false);
  const [leadRecoveryData, setLeadRecoveryData] = useState<LeadRecoveryData | null>(null);
  const [isLeadRecovering, setIsLeadRecovering] = useState(false);
  const [insertComposerText, setInsertComposerText] = useState<{ nonce: number; text: string } | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    setPinnedConversationIds(getPinnedConversationIds(workspaceId));
  }, [workspaceId]);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedConversationIds(new Set());
    setSelectedConversationsForBulk([]);
  }, []);

  const handleToggleConversationSelection = useCallback((conversation: MappedConversation) => {
    setSelectedConversationIds((prev) => {
      const next = new Set(prev);
      if (next.has(conversation.conversation_id)) {
        next.delete(conversation.conversation_id);
      } else {
        next.add(conversation.conversation_id);
      }
      return next;
    });
  }, []);

  const handleEnterSelectionWithConversation = useCallback((conversation: MappedConversation) => {
    setIsSelectionMode(true);
    setSelectedConversationIds(new Set([conversation.conversation_id]));
  }, []);

  const handleSelectAllVisible = useCallback((conversationIds: string[]) => {
    setSelectedConversationIds(new Set(conversationIds));
  }, []);

  const handleBulkActionsComplete = useCallback(() => {
    const affectedIds = new Set(selectedConversationIds);
    if (selectedConversation?.conversation_id && affectedIds.has(selectedConversation.conversation_id)) {
      setSelectedConversation(null);
    }
    exitSelectionMode();
    setConversationsRefreshKey((prev) => prev + 1);
  }, [exitSelectionMode, selectedConversation?.conversation_id, selectedConversationIds]);

  const conversationListSelectionProps = {
    selectionMode: isSelectionMode,
    selectedConversationIds,
    onToggleConversationSelection: handleToggleConversationSelection,
    onEnterSelectionWithConversation: handleEnterSelectionWithConversation,
    onSelectAllVisible: handleSelectAllVisible,
    onSelectedConversationsChange: setSelectedConversationsForBulk,
    onPinnedIdsChange: setPinnedConversationIds,
    onVisibleSelectableIdsChange: setVisibleSelectableIds,
  };

  const allVisibleSelected =
    visibleSelectableIds.length > 0 &&
    visibleSelectableIds.every((id) => selectedConversationIds.has(id));

  const bulkActionsBar = workspaceId && isSelectionMode && selectedConversationsForBulk.length > 0 ? (
    <ConversationBulkActionsBar
      workspaceId={workspaceId}
      selectedConversations={selectedConversationsForBulk}
      pinnedConversationIds={pinnedConversationIds}
      onPinnedChange={setPinnedConversationIds}
      memberId={memberId}
      memberDisplayName={memberDisplayName}
      visibleSelectableCount={visibleSelectableIds.length}
      visibleSelectableIds={visibleSelectableIds}
      allVisibleSelected={allVisibleSelected}
      onSelectAllVisible={handleSelectAllVisible}
      workspaceTags={workspaceTags}
      onComplete={handleBulkActionsComplete}
      onCancel={exitSelectionMode}
    />
  ) : null;

  const handleLeadIdParam = useCallback(async (leadId: string) => {
    if (!workspaceId) return;
    const integrationFromUrl = searchParams.get("integration_id");
    const integrationHint =
      integrationFromUrl ||
      (selectedIntegrationId && !isUnassignedIntegrationFilter(selectedIntegrationId)
        ? selectedIntegrationId
        : integrationFromUrl);
    const channelFromUrl = parseConversationChannel(searchParams.get("channel"));
    const selectCols =
      "id, channel, lead_id, integration_id, external_user_id, state, followup_enabled, metadata, is_closed";

    const fetchLatest = async (opts: {
      channel?: ConversationChannel | null;
      integrationId?: string | null;
    }) => {
      let q = supabase
        .from("conversations")
        .select(selectCols)
        .eq("lead_id", leadId)
        .eq("workspace_id", workspaceId);
      if (opts.channel) {
        q = q.eq("channel", opts.channel);
      }
      if (opts.integrationId && isUnassignedIntegrationFilter(opts.integrationId)) {
        q = q.is("integration_id", null);
      } else if (opts.integrationId) {
        q = q.eq("integration_id", opts.integrationId);
      }
      const { data } = await q
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    };

    let conv =
      (await fetchLatest({
        channel: channelFromUrl,
        integrationId: integrationHint,
      })) ?? null;

    if (!conv && (channelFromUrl || integrationHint)) {
      conv = (await fetchLatest({})) ?? null;
    }

    if (conv) {
      const convData = conv as any;
      const { data: lead } = await supabase
        .from("leads")
        .select("name, phone, assigned_member_id")
        .eq("id", leadId)
        .single();

      setSelectedChannel(convData.channel as ConversationChannel);
      setSelectedIntegrationId(convData.integration_id ?? null);
      setSelectedConversation({
        conversation_id: convData.id,
        integration_id: convData.integration_id ?? null,
        channel: convData.channel,
        lead_id: convData.lead_id || leadId,
        lead_name: lead?.name || "Lead",
        lead_phone: lead?.phone || null,
        state: convData.state || "active",
        followup_enabled: convData.followup_enabled,
        metadata: convData.metadata,
        is_closed: convData.is_closed || false,
        assigned_member_id: (lead as any)?.assigned_member_id ?? null,
      });
    }
    setSearchParams({}, { replace: true });
  }, [workspaceId, searchParams, selectedIntegrationId, setSearchParams]);

  const openStartedConversation = useCallback(
    async (lead: { id: string; name: string; phone: string | null; metadata: unknown; assigned_member_id?: string | null }, conversation: {
      id: string;
      channel: string;
      state?: string;
      followup_enabled?: boolean;
      is_closed?: boolean;
      integration_id?: string | null;
    }, isNew: boolean) => {
      setSelectedChannel("whatsapp");
      setSelectedIntegrationId(conversation.integration_id ?? null);
      setSelectedConversation({
        conversation_id: conversation.id,
        integration_id: conversation.integration_id ?? null,
        channel: conversation.channel,
        lead_id: lead.id,
        lead_name: lead.name,
        lead_phone: lead.phone,
        state: conversation.state || "active",
        followup_enabled: conversation.followup_enabled ?? true,
        metadata: lead.metadata,
        is_closed: conversation.is_closed || false,
        assigned_member_id: lead.assigned_member_id ?? null,
      });
      setConversationsRefreshKey((key) => key + 1);
      toast({
        title: isNew ? "Nova conversa criada" : "Conversa existente",
        description: isNew
          ? `Conversa iniciada com ${lead.name}`
          : `Abrindo conversa com ${lead.name}`,
      });
    },
    [toast],
  );

  const handleSwitchLeadPhone = useCallback(
    (conversation: {
      id: string;
      external_user_id: string | null;
      state?: string;
      followup_enabled?: boolean;
      is_closed?: boolean;
      integration_id?: string | null;
      channel: string;
    }) => {
      setSelectedConversation((prev) =>
        prev
          ? {
              ...prev,
              conversation_id: conversation.id,
              integration_id: conversation.integration_id ?? prev.integration_id,
              channel: conversation.channel,
              lead_phone: conversation.external_user_id || prev.lead_phone,
              state: conversation.state || prev.state,
              followup_enabled: conversation.followup_enabled ?? prev.followup_enabled,
              is_closed: conversation.is_closed || false,
            }
          : prev,
      );
      setConversationsRefreshKey((key) => key + 1);
    },
    [],
  );

  const startNewLeadConversation = useCallback(
    async (leadId: string, integrationId?: string | null) => {
      if (!workspaceId) return;

      const { data: lead } = await supabase
        .from("leads")
        .select("id, name, phone, metadata, assigned_member_id")
        .eq("id", leadId)
        .eq("workspace_id", workspaceId)
        .single();

      if (!lead) {
        toast({ title: "Lead não encontrado", variant: "destructive" });
        return;
      }

      if (!lead.phone) {
        toast({
          title: "Lead sem telefone",
          description: "Cadastre um telefone para iniciar conversa no WhatsApp.",
          variant: "destructive",
        });
        return;
      }

      const result = await startLeadConversation({
        workspaceId,
        leadId: lead.id,
        channel: "whatsapp",
        integrationId,
      });

      if (!result.ok) {
        if (result.needsIntegrationChoice) {
          setPendingNewLeadId(lead.id);
          setNewLeadIntegrationChoices(result.integrations);
          setNewLeadIntegrationChoiceOpen(true);
          return;
        }

        if (result.conflict?.can_see && result.conflict.lead_id) {
          const target = result.conflict.lead_id;
          toast({
            title: "Número já cadastrado",
            description: result.error,
            variant: "destructive",
            action: (
              <ToastAction altText="Abrir contato" onClick={() => void startNewLeadConversation(target, integrationId)}>
                Abrir contato
              </ToastAction>
            ),
          });
          return;
        }
        toast({
          title: result.conflict ? "Número já cadastrado" : "Erro ao iniciar conversa",
          description: result.error,
          variant: "destructive",
        });
        return;
      }

      await openStartedConversation(lead, result.conversation, result.isNew);
      setPendingNewLeadId(null);
      setNewLeadIntegrationChoiceOpen(false);
    },
    [workspaceId, toast, openStartedConversation],
  );

  const handleNewLeadParam = useCallback(async (leadId: string) => {
    if (!workspaceId || !user?.id) return;

    try {
      const integrationFromUrl = searchParams.get("integration_id");
      if (integrationFromUrl) {
        await startNewLeadConversation(leadId, integrationFromUrl);
        return;
      }

      const accessibleIntegrations = await fetchMemberAccessibleWhatsAppIntegrations(
        workspaceId,
        user.id,
      );

      if (accessibleIntegrations.length === 0) {
        toast({
          title: "Nenhuma conexão disponível",
          description: "Você não tem acesso a nenhuma conexão WhatsApp ativa.",
          variant: "destructive",
        });
        return;
      }

      if (accessibleIntegrations.length === 1) {
        await startNewLeadConversation(leadId, accessibleIntegrations[0].id);
        return;
      }

      setPendingNewLeadId(leadId);
      setNewLeadIntegrationChoices(accessibleIntegrations);
      setNewLeadIntegrationChoiceOpen(true);
    } catch (err) {
      console.error("handleNewLeadParam error:", err);
      toast({ title: "Erro ao iniciar conversa", variant: "destructive" });
    }
  }, [workspaceId, user?.id, searchParams, startNewLeadConversation, toast]);

  const handleConversationIdParam = useCallback(
    async (conversationId: string) => {
      if (!workspaceId) return;
      const { data: conv } = await supabase
        .from("conversations")
        .select(
          "id, channel, lead_id, integration_id, external_user_id, state, followup_enabled, metadata, is_closed",
        )
        .eq("id", conversationId)
        .eq("workspace_id", workspaceId)
        .maybeSingle();

      if (!conv) {
        toast({
          title: "Conversa indisponível",
          description: "Ela foi removida ou você não tem acesso a ela. Peça acesso a um administrador.",
          variant: "destructive",
        });
        setSearchParams({}, { replace: true });
        return;
      }

      const convData = conv as any;
      let leadName = "Lead";
      let leadPhone: string | null = null;
      let leadAssignedMemberId: string | null = null;
      if (convData.lead_id) {
        const { data: lead } = await supabase
          .from("leads")
          .select("name, phone, assigned_member_id")
          .eq("id", convData.lead_id)
          .maybeSingle();
        if (lead?.name) leadName = lead.name;
        leadPhone = lead?.phone ?? null;
        leadAssignedMemberId = (lead as any)?.assigned_member_id ?? null;
      }

      setSelectedChannel(convData.channel as ConversationChannel);
      setSelectedIntegrationId(convData.integration_id ?? null);
      setSelectedConversation({
        conversation_id: convData.id,
        integration_id: convData.integration_id ?? null,
        channel: convData.channel,
        lead_id: convData.lead_id || convData.external_user_id || "",
        lead_name: leadName,
        lead_phone: leadPhone,
        state: convData.state || "active",
        followup_enabled: convData.followup_enabled,
        metadata: convData.metadata,
        is_closed: convData.is_closed || false,
        assigned_member_id: leadAssignedMemberId,
      });

      // Marcar conversa como lida ao abrir via URL (notificações, deep-links, etc.)
      // O handler de clique na lista só dispara quando o usuário clica na linha; o
      // fluxo via URL precisa replicar o reset para sincronizar badge e DB.
      try {
        await markConversationRead(convData.id);
      } catch {
        // best-effort
      }
      window.dispatchEvent(
        new CustomEvent("conversation-read", {
          detail: { conversationId: convData.id },
        }),
      );

      setSearchParams({}, { replace: true });
    },
    [workspaceId, setSearchParams, toast],
  );

  useEffect(() => {
    const leadId = searchParams.get("lead_id");
    const newLeadId = searchParams.get("new_lead_id");
    const conversationId = searchParams.get("conversation_id");

    if (conversationId && workspaceId) {
      handleConversationIdParam(conversationId);
    } else if (leadId && workspaceId) {
      handleLeadIdParam(leadId);
    } else if (newLeadId && workspaceId) {
      handleNewLeadParam(newLeadId);
      setSearchParams({}, { replace: true });
    }
  }, [
    searchParams,
    workspaceId,
    handleConversationIdParam,
    handleLeadIdParam,
    handleNewLeadParam,
    setSearchParams,
  ]);

  useEffect(() => {
    if (selectedChannel === 'instagram') {
      setNewConversationOpen(false);
    }
  }, [selectedChannel]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { leadId?: string } | undefined;
      if (!detail?.leadId) return;
      if (selectedConversation?.lead_id === detail.leadId) {
        handleOpenLeadDetails();
      }
    };
    window.addEventListener("open-lead-details", handler);
    return () => window.removeEventListener("open-lead-details", handler);
  }, [selectedConversation]);
  const handleOpenLeadDetails = async () => {
    if (!selectedConversation) return;
    const {
      data: lead,
      error
    } = await supabase.from('leads').select('*').eq('id', selectedConversation.lead_id).single();
    if (!error && lead) {
      setFullLead(lead as Lead);
      setLeadDrawerOpen(true);
    } else {
      toast({
        title: "Erro ao carregar lead",
        description: "Não foi possível carregar os dados do lead.",
        variant: "destructive"
      });
    }
  };
  const handlePauseConversation = async (conversationId: string) => {
    try {
      const { error } = await supabase.rpc('execute_human_handoff' as any, {
        _conversation_id: conversationId,
        _member_id: memberId ?? null,
        _reason: 'manual_toggle',
      });
      if (error) throw error;

      if (memberId && workspaceId) {
        const memberName = memberDisplayName ?? "Membro";
        const { error: insertErr } = await supabase.from("member_activity_log").insert({
          workspace_id: workspaceId,
          member_id: memberId,
          conversation_id: conversationId,
          action_type: "ai_disabled",
          metadata: { member_name: memberName }
        });
        if (insertErr) console.error("Erro ao inserir log ai_disabled:", insertErr);
      }

      setSelectedConversation(prev => {
        if (!prev || prev.conversation_id !== conversationId) return prev;
        return {
          ...prev,
          state: "paused",
          followup_enabled: false,
        };
      });
      toast({
        title: "✅ Agente pausado",
        description: "O agente não enviará mais respostas automáticas e o follow-up foi desativado."
      });
    } catch (error) {
      console.error("Error pausing conversation:", error);
      toast({
        title: "❌ Erro ao pausar",
        description: "Não foi possível pausar o agente.",
        variant: "destructive"
      });
    }
  };
  const leadBlocksAgentResume = (conv: SelectedConversation | null): boolean => {
    if (!conv) return false;
    if (conv.stage_auto_disables_agent) return true;
    return conv.lead_stage === "atendimento_humano";
  };

  const handleResumeConversation = async (conversationId: string, leadId?: string) => {
    const resolvedLeadId = leadId ?? selectedConversation?.lead_id;
    if (resolvedLeadId) {
      const { data: blocked, error: blockError } = await supabase.rpc(
        "lead_has_agent_disabled_stage",
        { p_lead_id: resolvedLeadId },
      );
      if (blockError) {
        console.error("lead_has_agent_disabled_stage:", blockError);
      } else if (blocked === true) {
        toast(AGENT_BLOCKED_RESUME_TOAST);
        return;
      }
    }

    try {
      const { error } = await supabase.rpc('change_conversation_state' as any, {
        _conversation_id: conversationId,
        _new_state: 'active',
        _reason: 'manual_toggle',
        _actor_member_id: memberId ?? null,
        _enable_followup: true,
        // Reativação manual deve devolver o agente completo (RAG + ferramentas),
        // limpando o estado de escalonamento residual.
        _clear_escalated: true,
        _clear_pending_handoff: true,
      });
      if (error) throw error;

      try {
        await supabase.rpc('clear_conversation_inbound_guard' as any, {
          p_conversation_id: conversationId,
          p_human_bypass_hours: 0,
        });
      } catch (e) {
        console.warn('clear_conversation_inbound_guard failed', e);
      }

      if (memberId && workspaceId) {
        const memberName = memberDisplayName ?? "Membro";
        const { error: insertErr } = await supabase.from("member_activity_log").insert({
          workspace_id: workspaceId,
          member_id: memberId,
          conversation_id: conversationId,
          action_type: "ai_enabled",
          metadata: { member_name: memberName }
        });
        if (insertErr) console.error("Erro ao inserir log ai_enabled:", insertErr);
      }
      setSelectedConversation(prev => {
        if (!prev || prev.conversation_id !== conversationId) return prev;
        return {
          ...prev,
          state: "active",
          followup_enabled: true,
        };
      });
      toast({
        title: "✅ Agente retomado",
        description: "O agente voltará a enviar respostas automáticas."
      });
    } catch (error: any) {
      console.error("Error resuming conversation:", error);
      const message = String(error?.message ?? error?.details ?? "");
      if (message.includes("lead_stage_blocks_agent_resume")) {
        toast(AGENT_BLOCKED_RESUME_TOAST);
        return;
      }
      toast({
        title: "❌ Erro ao retomar",
        description: "Não foi possível retomar o agente.",
        variant: "destructive"
      });
    }
  };

  const handleEscalationResolved = useCallback(
    async (result: ResolveEscalationResult) => {
      const conv = selectedConversation;
      if (!conv) return;

      const snapshot = applyEscalationResolvedLocally({
        conversationId: conv.conversation_id,
        leadId: conv.lead_id,
        result,
        previousState: conv.state,
        previousFollowupEnabled: conv.followup_enabled,
        previousStage: conv.lead_stage,
        previousStageBlocksAgent: conv.stage_auto_disables_agent,
      });

      setSelectedConversation((prev) => {
        if (!prev || prev.conversation_id !== conv.conversation_id) return prev;
        return {
          ...prev,
          state: snapshot.state,
          followup_enabled: snapshot.followup_enabled ?? prev.followup_enabled,
          lead_stage: snapshot.lead_stage ?? prev.lead_stage,
          stage_auto_disables_agent: snapshot.stage_auto_disables_agent ?? prev.stage_auto_disables_agent,
        };
      });
    },
    [selectedConversation],
  );

  const syncConversationClosedInInbox = useCallback((conversationId: string) => {
    dispatchConversationInboxRowPatch({
      conversationId,
      isClosed: true,
    });
    setSelectedConversation((prev) => {
      if (!prev || prev.conversation_id !== conversationId) return prev;
      return { ...prev, is_closed: true };
    });
  }, []);

  const syncConversationReopenedInInbox = useCallback((conversationId: string) => {
    dispatchConversationInboxRowPatch({
      conversationId,
      isClosed: false,
    });
    setStatusFilter("open");
    setSelectedConversation((prev) => {
      if (!prev || prev.conversation_id !== conversationId) return prev;
      return { ...prev, is_closed: false };
    });
  }, []);

  const handleCloseConversation = async (
    conversationId: string,
    options?: { preserveAssignees?: boolean },
  ) => {
    const preserveAssignees = options?.preserveAssignees ?? false;
    const leadIdFromSelection =
      selectedConversation?.conversation_id === conversationId
        ? selectedConversation.lead_id
        : null;

    // Marca como encerrada na lista antes de limpar atribuição — evita reaparecer em Todas/Abertas.
    syncConversationClosedInInbox(conversationId);

    try {
      const {
        error
      } = await supabase.from("conversations").update({
        is_closed: true,
        closed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as any).eq("id", conversationId);
      if (error) throw error;

      if (!preserveAssignees) {
        // Devolve o lead ao "pool": limpa atribuição para que volte a ficar visível para todos os membros
        try {
          let leadIdToClear = leadIdFromSelection;
          if (!leadIdToClear) {
            const { data: convRow } = await supabase
              .from("conversations")
              .select("lead_id")
              .eq("id", conversationId)
              .maybeSingle();
            leadIdToClear = convRow?.lead_id ?? null;
          }
          if (leadIdToClear) {
            await supabase.rpc("remove_all_lead_assignees", {
              p_lead_id: leadIdToClear,
            });
            dispatchConversationLeadPatch({
              leadId: leadIdToClear,
              assignedMemberId: null,
              assignedMemberIds: [],
              assignedMemberLabel: null,
            });
          }
        } catch (clearErr) {
          console.error("Erro ao limpar atribuição do lead ao encerrar:", clearErr);
        }
      }

      if (memberId && workspaceId) {
        const memberName = memberDisplayName ?? "Membro";
        const { error: insertErr } = await supabase.from("member_activity_log").insert({
          workspace_id: workspaceId,
          member_id: memberId,
          conversation_id: conversationId,
          action_type: "conversation_closed",
          metadata: {
            member_name: memberName,
            preserve_assignees: preserveAssignees,
          }
        });
        if (insertErr) console.error("Erro ao inserir log conversation_closed:", insertErr);
      }

      toast({
        title: "✅ Conversa encerrada",
        description: preserveAssignees
          ? "A conversa foi encerrada e os responsáveis foram mantidos."
          : "Esta conversa foi marcada como encerrada."
      });
    } catch (error) {
      syncConversationReopenedInInbox(conversationId);
      console.error("Error closing conversation:", error);
      toast({
        title: "❌ Erro ao encerrar",
        description: "Não foi possível encerrar a conversa.",
        variant: "destructive"
      });
    }
  };

  // Interceptor: para WhatsApp, abre dialog de agendamento; outros canais fecham direto
  const handleMaybeClose = async (
    conversationId: string,
    preserveAssignees = false,
  ) => {
    if (
      !preserveAssignees
      && selectedConversation?.channel === 'whatsapp'
      && !selectedConversation?.is_closed
    ) {
      setScheduleCloseOpen(true);
    } else {
      await handleCloseConversation(conversationId, { preserveAssignees });
    }
  };

  const handleReopenConversation = async (conversationId: string) => {
    try {
      const { error } = await supabase.from("conversations").update({
        is_closed: false,
        closed_at: null,
        updated_at: new Date().toISOString()
      } as any).eq("id", conversationId);
      if (error) throw error;

      if (memberId && workspaceId) {
        const memberName = memberDisplayName ?? "Membro";
        const { error: insertErr } = await supabase.from("member_activity_log").insert({
          workspace_id: workspaceId,
          member_id: memberId,
          conversation_id: conversationId,
          action_type: "conversation_reopened",
          metadata: { member_name: memberName }
        });
        if (insertErr) console.error("Erro ao inserir log conversation_reopened:", insertErr);
      }

      syncConversationReopenedInInbox(conversationId);
      toast({
        title: "✅ Conversa reaberta",
        description: "A conversa foi reaberta."
      });
    } catch (error) {
      console.error("Error reopening conversation:", error);
      toast({
        title: "❌ Erro ao reabrir",
        description: "Não foi possível reabrir a conversa.",
        variant: "destructive"
      });
    }
  };
  const handleSummarizeConversation = async (conversationId: string) => {
    setIsSummarizing(true);
    setSummaryData(null);
    setShowSummarySheet(true);
    try {
      const { data, error } = await supabase.functions.invoke('summarize-conversation', {
        body: { conversation_id: conversationId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSummaryData(data);
    } catch (err: any) {
      toast({
        title: "Erro ao resumir atendimento",
        description: err.message || "Não foi possível gerar o resumo.",
        variant: "destructive",
      });
      setShowSummarySheet(false);
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleLeadRecovery = async (conversationId: string) => {
    if (!plusPlanAllowed) {
      toast({
        title: "Recurso exclusivo do plano Plus",
        description: "Faça upgrade para usar o Assistente de Recuperação de Leads.",
        variant: "destructive",
      });
      return;
    }

    setIsLeadRecovering(true);
    setLeadRecoveryData(null);
    setShowLeadRecoverySheet(true);
    try {
      const result = await supabase.functions.invoke("suggest-lead-recovery", {
        body: { conversation_id: conversationId },
      });
      const data = await (async () => {
        if (result.data) return result.data as LeadRecoveryData & { error?: string; message?: string };
        if (!result.error) return null;
        try {
          const response = (result.error as { context?: Response }).context;
          if (response && typeof response.json === "function") {
            return (await response.json()) as LeadRecoveryData & { error?: string; message?: string };
          }
        } catch {
          // ignore body parse errors
        }
        return null;
      })();

      if (data?.error === "plan_required") {
        toast({
          title: "Recurso exclusivo do plano Plus",
          description: data.message || "Faça upgrade para usar o Assistente de Recuperação de Leads.",
          variant: "destructive",
        });
        setShowLeadRecoverySheet(false);
        return;
      }
      if (result.error) throw result.error;
      if (data?.error) throw new Error(data.error);
      if (!data) throw new Error("Resposta vazia do servidor.");
      setLeadRecoveryData(data);
    } catch (err: any) {
      toast({
        title: "Erro ao recuperar lead",
        description: err.message || "Não foi possível gerar sugestões de recuperação.",
        variant: "destructive",
      });
      setShowLeadRecoverySheet(false);
    } finally {
      setIsLeadRecovering(false);
    }
  };

  const handleInsertRecoveryMessage = (text: string) => {
    setInsertComposerText({ nonce: Date.now(), text });
  };

  const handleClearContext = async (conversationId: string) => {
    setClearContextDialog({
      open: true,
      conversationId
    });
  };
  const [clearMessagesSignal, setClearMessagesSignal] = useState(0);
  const confirmClearContext = async () => {
    const conversationId = clearContextDialog.conversationId;
    if (!conversationId) return;
    try {
      const {
        error
      } = await supabase.from("messages").delete().eq("conversation_id", conversationId);
      if (error) throw error;

      await supabase.from("conversations").update({
        last_message_preview: null,
        last_message_at: null,
        last_message_direction: null,
        unread_count: 0,
        updated_at: new Date().toISOString(),
      }).eq("id", conversationId);

      setClearMessagesSignal(prev => prev + 1);

      setConversationsRefreshKey(k => k + 1);

      toast({
        title: "✅ Contexto limpo com sucesso",
        description: "Todas as mensagens foram deletadas. O agente iniciará um novo contexto."
      });
    } catch (error) {
      console.error("Error clearing context:", error);
      toast({
        title: "❌ Erro ao limpar contexto",
        description: error instanceof Error ? error.message : "Não foi possível deletar as mensagens.",
        variant: "destructive"
      });
    } finally {
      setClearContextDialog({
        open: false,
        conversationId: null
      });
    }
  };
  const handleToggleFollowup = async (conversationId: string, currentStatus: boolean) => {
    try {
      const {
        error
      } = await supabase.from("conversations").update({
        followup_enabled: !currentStatus,
        updated_at: new Date().toISOString()
      }).eq("id", conversationId);
      if (error) throw error;
      setSelectedConversation(prev => {
        if (!prev || prev.conversation_id !== conversationId) return prev;
        return {
          ...prev,
          followup_enabled: !currentStatus
        };
      });
      toast({
        title: !currentStatus ? "✅ Follow-up ativado" : "🔕 Follow-up desativado",
        description: !currentStatus ? "O sistema enviará mensagens de follow-up automaticamente" : "O sistema não enviará mais mensagens de follow-up para este chat"
      });
    } catch (error) {
      console.error("Error toggling followup:", error);
      toast({
        title: "❌ Erro ao alterar follow-up",
        description: "Não foi possível alterar o status de follow-up.",
        variant: "destructive"
      });
    }
  };
  const handleDeleteConversation = async (conversationId: string, leadName: string) => {
    setDeleteLeadDialog({
      open: true,
      leadId: conversationId,
      leadName
    });
  };
  const confirmDeleteLead = async () => {
    const conversationId = deleteLeadDialog.leadId;
    if (!conversationId) return;
    try {
      const { error: msgError } = await supabase
        .from("messages")
        .delete()
        .eq("conversation_id", conversationId);
      if (msgError) throw msgError;

      const { error: convError } = await supabase
        .from("conversations")
        .delete()
        .eq("id", conversationId);
      if (convError) throw convError;

      if (selectedConversation?.conversation_id === conversationId) {
        setSelectedConversation(null);
      }
      toast({
        title: "✅ Conversa excluída",
        description: "A conversa e suas mensagens foram removidas. O lead foi mantido."
      });

      setConversationsRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast({
        title: "❌ Erro ao excluir",
        description: error instanceof Error ? error.message : "Não foi possível excluir a conversa.",
        variant: "destructive"
      });
    } finally {
      setDeleteLeadDialog({
        open: false,
        leadId: null,
        leadName: null
      });
    }
  };
  if (!workspaceId && workspaceLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-primary/50 animate-pulse mx-auto mb-4"></div>
          <Sparkles className="w-6 h-6 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    </div>;
  }
  if (!workspaceId && !workspaceLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Nenhum workspace encontrado.</p>
      </div>
    </div>;
  }
  const getChannelGradient = (channel: string) => {
    switch (channel) {
      case "whatsapp":
        return "from-green-500 to-emerald-600";
      case "instagram":
        return "from-pink-500 to-rose-600";
      default:
        return "from-blue-500 to-cyan-600";
    }
  };
  if (selectedChannel === 'instagram' && instagramView === 'comments') {
    return <div className={mobileShellClassName}>
        <Card className="flex flex-col flex-1 min-h-0 border-0 shadow-lg rounded-none">
          <CardHeader className="flex-shrink-0 pb-3 sm:pb-4 border-b sm:px-6 px-[21px] py-[16px]">
            <div className="flex items-center gap-2">
            <ChannelToggle 
              value={selectedChannel} 
              onChange={(value) => {
                setSelectedChannel(value);
                setSelectedIntegrationId(null);
                setSelectedConversation(null);
                if (value === 'instagram') {
                  setInstagramView('inbox');
                }
              }}
              workspaceId={workspaceId}
              selectedIntegrationId={selectedIntegrationId}
              onIntegrationSelect={setSelectedIntegrationId}
              isOwner={isOwner}
              className="shrink-0"
            />

              <InstagramViewTabs
                value={instagramView}
                onChange={setInstagramView}
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0 min-h-0">
            <InstagramCommentsView workspaceId={workspaceId} />
          </CardContent>
        </Card>
      </div>;
  }

  const listTopBar = <>
      {/* Controles do topo (layout adaptativo para evitar overflow no painel esquerdo do desktop) */}
      {isMobile ? <div className="space-y-2">
          <div className="flex items-center gap-2">
            <ChannelToggle
              value={selectedChannel}
              onChange={handleChannelChange}
              workspaceId={workspaceId}
              selectedIntegrationId={selectedIntegrationId}
              onIntegrationSelect={setSelectedIntegrationId}
              isOwner={isOwner}
              className="shrink-0"
            />
            {selectedChannel === "instagram" && (
              <InstagramViewTabs
                value={instagramView}
                onChange={setInstagramView}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
          
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Buscar por nome ou telefone..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" />
          </div>
          <SortAndFilterMenu
            prioritizeUnread={prioritizeUnread}
            setPrioritizeUnread={setPrioritizeUnread}
            unreadCount={filterCounts.unread}
            workspaceTags={workspaceTags}
            selectedTagIds={selectedTagIds}
            setSelectedTagIds={setSelectedTagIds}
            filterUpcomingAppointments={filterUpcomingAppointments}
            setFilterUpcomingAppointments={setFilterUpcomingAppointments}
            filterCancelledAppointments={filterCancelledAppointments}
            setFilterCancelledAppointments={setFilterCancelledAppointments}
            filterUnreadOnly={filterUnreadOnly}
            setFilterUnreadOnly={setFilterUnreadOnly}
          />

          {selectedChannel === 'whatsapp' && <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setNewConversationOpen(true)} title="Nova conversa">
              <SquarePen className="w-5 h-5" />
            </Button>}
        </div>
        </div> : selectedChannel === 'instagram' ? <div className="space-y-2">
          <div className="flex items-center gap-2">
          <ChannelToggle 
            value={selectedChannel} 
            onChange={(value) => {
              setSelectedChannel(value);
              setSelectedIntegrationId(null);
              setSelectedConversation(null);
              if (value === 'instagram') setInstagramView('inbox');
            }}
            workspaceId={workspaceId}
            selectedIntegrationId={selectedIntegrationId}
            onIntegrationSelect={setSelectedIntegrationId}
            isOwner={isOwner}
            className="shrink-0"
          />

            <InstagramViewTabs
              value={instagramView}
              onChange={setInstagramView}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por nome ou telefone..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" />
            </div>
            <SortAndFilterMenu
              prioritizeUnread={prioritizeUnread}
              setPrioritizeUnread={setPrioritizeUnread}
              unreadCount={filterCounts.unread}
              workspaceTags={workspaceTags}
              selectedTagIds={selectedTagIds}
              setSelectedTagIds={setSelectedTagIds}
              filterUpcomingAppointments={filterUpcomingAppointments}
              setFilterUpcomingAppointments={setFilterUpcomingAppointments}
              filterCancelledAppointments={filterCancelledAppointments}
              setFilterCancelledAppointments={setFilterCancelledAppointments}
              filterUnreadOnly={filterUnreadOnly}
              setFilterUnreadOnly={setFilterUnreadOnly}
            />

          </div>
        </div> : <div className="flex items-center gap-2">
          <ChannelToggle 
            value={selectedChannel} 
            onChange={(value) => {
              setSelectedChannel(value);
              setSelectedIntegrationId(null);
              setSelectedConversation(null);
              if (value === 'instagram') setInstagramView('inbox');
            }}
            workspaceId={workspaceId}
            selectedIntegrationId={selectedIntegrationId}
              onIntegrationSelect={setSelectedIntegrationId}
              isOwner={isOwner}
            />

          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Buscar por nome ou telefone..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" />
          </div>
          <SortAndFilterMenu
            prioritizeUnread={prioritizeUnread}
            setPrioritizeUnread={setPrioritizeUnread}
            unreadCount={filterCounts.unread}
            workspaceTags={workspaceTags}
            selectedTagIds={selectedTagIds}
            setSelectedTagIds={setSelectedTagIds}
            filterUpcomingAppointments={filterUpcomingAppointments}
            setFilterUpcomingAppointments={setFilterUpcomingAppointments}
            filterCancelledAppointments={filterCancelledAppointments}
            setFilterCancelledAppointments={setFilterCancelledAppointments}
            filterUnreadOnly={filterUnreadOnly}
            setFilterUnreadOnly={setFilterUnreadOnly}
          />


          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setNewConversationOpen(true)} title="Nova conversa">
            <SquarePen className="w-5 h-5" />
          </Button>
        </div>}
      <ConversationInboxFilters
        attentionFilter={attentionFilter}
        setAttentionFilter={setAttentionFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        filterCounts={filterCounts}
        tabOrder={inboxTabOrder}
        onSaveTabOrder={handleSaveTabOrder}
      />
      {selectedTagIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-1 pt-1">
          {selectedTagIds.map((id) => {
            const tag = workspaceTags.find((t) => t.id === id);
            if (!tag) return null;
            return (
              <LeadTagBadge
                key={id}
                tag={tag}
                size="sm"
                onRemove={() => setSelectedTagIds(selectedTagIds.filter((t) => t !== id))}
              />
            );
          })}
          <button
            type="button"
            onClick={() => setSelectedTagIds([])}
            className="text-[10px] text-muted-foreground hover:text-foreground underline ml-1"
          >
            Limpar
          </button>
        </div>
      )}
    </>;


  if (isMobile) {
    return <div className={mobileShellClassName}>
        {selectedConversation ? (/* Tela de chat em tela cheia */
      <Card className="flex flex-col flex-1 min-h-0 border-0 shadow-lg rounded-none">
            <CardHeader className="flex-shrink-0 border-b px-3 py-2.5">
              <div className="flex min-w-0 flex-col gap-2">
                <div className="flex min-w-0 items-center gap-2">
                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 -ml-1" onClick={() => setSelectedConversation(null)} title="Voltar">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Avatar className="w-9 h-9 shrink-0">
                  {(selectedConversation.metadata?.instagram_profile_pic || selectedConversation.metadata?.profile_picture) ? (
                    <AvatarImage src={selectedConversation.metadata.instagram_profile_pic || selectedConversation.metadata.profile_picture} alt={selectedConversation.lead_name} className="object-cover" />
                  ) : null}
                  <AvatarFallback className="bg-gradient-to-br from-primary to-primary/50 text-white font-semibold text-sm">
                    {selectedConversation.lead_name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="min-w-0 truncate font-semibold text-sm leading-tight">
                      {selectedConversation.lead_name}
                    </span>
                    <ConversationAttendingNotice
                      workspaceId={workspaceId}
                      assignedMemberId={selectedConversation.assigned_member_id}
                      assignedMemberIds={selectedConversation.assigned_member_ids}
                      conversationState={selectedConversation.state}
                    />
                    {selectedConversation.is_closed && (
                      <Badge variant="secondary" className="h-5 shrink-0 bg-muted px-1.5 py-0 text-[10px] font-medium text-muted-foreground">
                        Encerrado
                      </Badge>
                    )}
                  </div>
                  {selectedConversation.metadata?.instagram_username && <p className="text-xs text-muted-foreground truncate">@{selectedConversation.metadata.instagram_username}</p>}
                  {selectedConversation.channel === "whatsapp" && workspaceId ? (
                    <div className="text-xs text-muted-foreground">
                      <ConversationPhoneSwitch
                        workspaceId={workspaceId}
                        leadId={selectedConversation.lead_id}
                        leadName={selectedConversation.lead_name}
                        integrationId={selectedConversation.integration_id ?? null}
                        channel={selectedConversation.channel}
                        currentConversationId={selectedConversation.conversation_id}
                        currentNumber={selectedConversation.lead_phone}
                        onSwitched={handleSwitchLeadPhone}
                      />
                    </div>
                  ) : (
                    selectedConversation.lead_phone && !selectedConversation.metadata?.instagram_username && (
                      <p className="text-xs text-muted-foreground truncate">{selectedConversation.lead_phone}</p>
                    )
                  )}
                  {selectedConversation.originLabel && <p className="text-[11px] text-muted-foreground truncate">Via: {selectedConversation.originLabel}</p>}
                </div>
                <ResolveEscalationButton
                  leadId={selectedConversation.lead_id}
                  conversationId={selectedConversation.conversation_id}
                  workspaceId={workspaceId}
                  memberId={memberId}
                  integrationId={selectedConversation.integration_id}
                  onResolved={handleEscalationResolved}
                  showLabel
                />
                <div
                  className="flex shrink-0 items-center gap-1.5 self-center"
                  title={selectedConversation.state === "active" ? "Agente IA ativo — toque para pausar" : "Agente IA pausado — toque para ativar"}
                >
                  <span className="text-[10px] font-medium text-muted-foreground whitespace-nowrap">IA</span>
                  <Switch
                    checked={selectedConversation.state === "active"}
                    disabled={leadBlocksAgentResume(selectedConversation)}
                    onCheckedChange={checked => {
                      if (checked) {
                        if (leadBlocksAgentResume(selectedConversation)) {
                          toast(AGENT_BLOCKED_RESUME_TOAST);
                          return;
                        }
                        handleResumeConversation(selectedConversation.conversation_id);
                      } else {
                        handlePauseConversation(selectedConversation.conversation_id);
                      }
                    }}
                    className="data-[state=checked]:bg-primary h-5 w-9 shrink-0"
                  />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                    <DropdownMenuItem
                      onSelect={() => {
                        mobileAssignRef.current?.open();
                      }}
                    >
                      <UserPlus className="w-4 h-4 mr-2" /> Atribuir membro
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setTimeout(() => handleOpenLeadDetails(), 0)}>
                      <User className="w-4 h-4 mr-2" /> Informações do Lead
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => handleSummarizeConversation(selectedConversation.conversation_id), 0); }} disabled={isSummarizing}>
                      <Sparkles className="w-4 h-4 mr-2" /> {isSummarizing ? "Resumindo..." : "Resumir Atendimento"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => handleLeadRecovery(selectedConversation.conversation_id), 0); }} disabled={isLeadRecovering}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {isLeadRecovering ? "Analisando..." : plusPlanAllowed ? "Recuperar Lead" : "Recuperar Lead (Plus)"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleToggleFollowup(selectedConversation.conversation_id, selectedConversation.followup_enabled)}>
                      {selectedConversation.followup_enabled ? <><BellOff className="w-4 h-4 mr-2" /> Desativar Follow-up</> : <><Bell className="w-4 h-4 mr-2" /> Ativar Follow-up</>}
                    </DropdownMenuItem>
                    {selectedConversation.is_closed ? (
                      <DropdownMenuItem onClick={() => handleReopenConversation(selectedConversation.conversation_id)}>
                        <RotateCcw className="w-4 h-4 mr-2" /> Reabrir conversa
                      </DropdownMenuItem>
                    ) : (
                      <>
                        <DropdownMenuItem
                          onSelect={() => {
                            setTimeout(() => {
                              void handleMaybeClose(selectedConversation.conversation_id);
                            }, 0);
                          }}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" /> Encerrar conversa
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            setTimeout(() => {
                              void handleMaybeClose(selectedConversation.conversation_id, true);
                            }, 0);
                          }}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" /> Encerrar sem desatribuir
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem onSelect={() => setTimeout(() => handleClearContext(selectedConversation.conversation_id), 0)} className="text-destructive">Limpar Contexto</DropdownMenuItem>
                    {selectedConversation.channel === 'instagram' && <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={async () => {
                    try {
                      const {
                        error
                      } = await supabase.functions.invoke('update-instagram-user-data', {
                        body: {
                          leadId: selectedConversation.lead_id
                        }
                      });
                      if (error) throw error;
                      toast({
                        title: "✅ Dados atualizados",
                        description: "Os dados do Instagram foram atualizados com sucesso"
                      });
                      window.location.reload();
                    } catch (error: any) {
                      toast({
                        title: "❌ Erro ao atualizar",
                        description: error.message || "Não foi possível atualizar os dados",
                        variant: "destructive"
                      });
                    }
                  }}>Atualizar dados do Instagram</DropdownMenuItem>
                      </>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setTimeout(() => handleDeleteConversation(selectedConversation.conversation_id, selectedConversation.lead_name), 0)} className="text-destructive focus:text-destructive">
                      <Trash2 className="w-4 h-4 mr-2" /> Excluir Conversa
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <ChannelToggle
                    value={selectedChannel}
                    onChange={handleChannelChange}
                    workspaceId={workspaceId}
                    selectedIntegrationId={selectedIntegrationId}
                    onIntegrationSelect={setSelectedIntegrationId}
                    isOwner={isOwner}
                    className="shrink-0"
                  />
                  <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-1.5">
                  {workspaceId ? (
                    <>
                      <LeadTagsPopover
                        workspaceId={workspaceId}
                        leadId={selectedConversation.lead_id}
                        compact
                      />
                      <MemberAssignPopover
                        ref={mobileAssignRef}
                        hideTrigger
                        showTrigger
                        compact
                        workspaceId={workspaceId}
                        leadId={selectedConversation.lead_id}
                        currentMemberIds={selectedConversation.assigned_member_ids}
                        onAssign={handleLeadMemberAssign}
                        conversationId={selectedConversation.conversation_id}
                        actingMemberId={memberId}
                      />
                    </>
                  ) : null}
                  <ConversationLeadFunnelControls
                    leadId={selectedConversation.lead_id}
                    workspaceId={workspaceId}
                    integrationId={selectedConversation.integration_id ?? null}
                  />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden min-h-0 flex flex-col p-1">
              <ChatInterface workspaceId={workspaceId} leadId={selectedConversation.lead_id} leadName={selectedConversation.lead_name} leadMetadata={selectedConversation.metadata} conversations={[{
            channel: selectedConversation.channel,
            conversation_id: selectedConversation.conversation_id,
            state: selectedConversation.state,
            followup_enabled: selectedConversation.followup_enabled,
            integration_id: selectedConversation.integration_id ?? null,
            originLabel: selectedConversation.originLabel ?? null,
          }]} onPauseConversation={handlePauseConversation} onResumeConversation={handleResumeConversation} onCloseConversation={handleMaybeClose} onToggleFollowup={handleToggleFollowup} onClearContext={handleClearContext} clearMessagesSignal={clearMessagesSignal} insertComposerText={insertComposerText} onLeadRecovery={handleLeadRecovery} isLeadRecovering={isLeadRecovering} leadRecoveryPlanAllowed={plusPlanAllowed} />
            </CardContent>
          </Card>) : (/* Tela de lista: 3 pontinhos, busca, nova conversa, filtros, conversas */
      <Card className="flex flex-col flex-1 min-h-0 border-0 shadow-lg rounded-none">
            <CardHeader className="flex-shrink-0 pb-3 border-b px-4 py-4 space-y-3">
              {listTopBar}
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-0 px-2 py-2 min-h-0">
              <ConversationList workspaceId={workspaceId} channel={selectedChannel} onSelectConversation={setSelectedConversation} selectedConversationId={undefined} refreshKey={conversationsRefreshKey} searchTerm={searchTerm} attentionFilter={attentionFilter} statusFilter={statusFilter} onCountsChange={setFilterCounts} integrationId={selectedIntegrationId} currentMemberId={memberId} prioritizeUnread={prioritizeUnread} filterUpcomingAppointments={filterUpcomingAppointments} filterCancelledAppointments={filterCancelledAppointments} filterUnreadOnly={filterUnreadOnly} isAdmin={isAdmin} conversationsVisibility={conversationsVisibility} selectedTagIds={selectedTagIds} onRequestClearContext={handleClearContext} {...conversationListSelectionProps} />
            </CardContent>
          </Card>)}

        {/* Portais mobile — mesmos componentes do desktop */}
        <AlertDialog open={clearContextDialog.open} onOpenChange={open => !open && setClearContextDialog({
          open: false,
          conversationId: null
        })}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader className="space-y-4">
              <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500" />
                </div>
                Limpar Contexto
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Esta ação irá remover permanentemente:
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/70">•</span>
                      Todas as mensagens desta conversa
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/70">•</span>
                      Todo o contexto e memória do agente
                    </li>
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-0">
              <AlertDialogCancel className="border-0 bg-transparent hover:bg-muted">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmClearContext} className="bg-destructive hover:bg-destructive/90">
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={deleteLeadDialog.open} onOpenChange={() => setDeleteLeadDialog({ open: false, leadId: null, leadName: null })}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader className="space-y-4">
              <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500" />
                </div>
                Confirmar Exclusão
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p className="text-sm">
                    <span className="text-muted-foreground">Excluir: </span>
                    <span className="font-semibold text-primary">{deleteLeadDialog.leadName || 'Lead'}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">Esta ação irá remover permanentemente:</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/70">•</span>
                      Esta conversa e todas as suas mensagens
                    </li>
                  </ul>
                  <p className="text-sm text-muted-foreground">O lead será mantido e poderá ser acessado na página de Leads.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-0">
              <AlertDialogCancel className="border-0 bg-transparent hover:bg-muted">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteLead} className="bg-destructive hover:bg-destructive/90">
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <LeadDetailDrawer
          open={leadDrawerOpen}
          onOpenChange={setLeadDrawerOpen}
          lead={fullLead}
          workspaceId={workspaceId}
          onUpdate={() => setConversationsRefreshKey((prev) => prev + 1)}
          onEscalationResolved={handleEscalationResolved}
          conversationId={selectedConversation?.conversation_id ?? null}
          memberId={memberId}
        />

        <ConversationSummarySheet open={showSummarySheet} onOpenChange={setShowSummarySheet} data={summaryData} isLoading={isSummarizing} />
        <LeadRecoverySheet
          open={showLeadRecoverySheet}
          onOpenChange={setShowLeadRecoverySheet}
          data={leadRecoveryData}
          isLoading={isLeadRecovering}
          onInsertToChat={handleInsertRecoveryMessage}
        />

        {workspaceId && selectedChannel === 'whatsapp' && <NewConversationDialog open={newConversationOpen} onOpenChange={setNewConversationOpen} workspaceId={workspaceId} channel="whatsapp" integrationId={selectedIntegrationId} onConversationCreated={conv => {
          setSelectedConversation(conv);
          setConversationsRefreshKey(prev => prev + 1);
        }} />}

        <IntegrationChoiceDialog
          open={newLeadIntegrationChoiceOpen}
          onOpenChange={(open) => {
            setNewLeadIntegrationChoiceOpen(open);
            if (!open) setPendingNewLeadId(null);
          }}
          integrations={newLeadIntegrationChoices}
          loading={isStartingNewLeadConversation}
          onConfirm={async (integrationId) => {
            if (!pendingNewLeadId) return;
            setIsStartingNewLeadConversation(true);
            try {
              await startNewLeadConversation(pendingNewLeadId, integrationId);
            } finally {
              setIsStartingNewLeadConversation(false);
            }
          }}
        />

        {/* Mesmo fluxo de encerrar/agendar do desktop — o branch mobile retorna cedo e não chega ao bloco desktop */}
        {selectedConversation && (
          <ScheduleCloseDialog
            open={scheduleCloseOpen}
            onOpenChange={setScheduleCloseOpen}
            conversationId={selectedConversation.conversation_id}
            leadName={selectedConversation.lead_name}
            workspaceId={workspaceId || ""}
            onJustClose={() => handleCloseConversation(selectedConversation.conversation_id)}
            onJustClosePreserveAssignees={() =>
              handleCloseConversation(selectedConversation.conversation_id, {
                preserveAssignees: true,
              })
            }
            onScheduledClose={(didClose) => {
              if (didClose) {
                syncConversationClosedInInbox(selectedConversation.conversation_id);
              }
            }}
          />
        )}
      </div>;
  }

  return <div className="flex h-full w-full min-w-0 flex-col overflow-hidden">
    <div className="grid min-h-0 min-w-0 flex-1 grid-cols-1 gap-0 overflow-hidden md:grid-cols-[minmax(0,min(38vw,_340px))_minmax(0,_1fr)] lg:grid-cols-[minmax(280px,_360px)_minmax(0,_1fr)]">
      <Card className="flex min-h-0 min-w-0 flex-col border-0 shadow-lg rounded-none border-r">
        <CardHeader className="flex-shrink-0 border-b px-2 py-3 sm:px-3 sm:py-4 space-y-3">
          {listTopBar}
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-0 px-2 py-2 sm:px-3 sm:py-3 min-h-0">
          <ConversationList workspaceId={workspaceId} channel={selectedChannel} onSelectConversation={setSelectedConversation} selectedConversationId={selectedConversation?.conversation_id} refreshKey={conversationsRefreshKey} searchTerm={searchTerm} attentionFilter={attentionFilter} statusFilter={statusFilter} onCountsChange={setFilterCounts} integrationId={selectedIntegrationId} currentMemberId={memberId} prioritizeUnread={prioritizeUnread} filterUpcomingAppointments={filterUpcomingAppointments} filterCancelledAppointments={filterCancelledAppointments} filterUnreadOnly={filterUnreadOnly} isAdmin={isAdmin} conversationsVisibility={conversationsVisibility} selectedTagIds={selectedTagIds} onRequestClearContext={handleClearContext} {...conversationListSelectionProps} />
        </CardContent>
      </Card>

      <Card className="flex min-h-0 min-w-0 flex-col border-0 shadow-lg rounded-none">
        <CardHeader className="min-w-0 flex-shrink-0 border-b px-3 py-2 sm:px-4 sm:py-2.5">
          <div className="flex min-w-0 flex-col gap-1.5">
            <div
              ref={chatHeaderMeasureRef}
              className="flex min-w-0 items-center gap-2"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                {selectedConversation ? <>
                  <Avatar className="h-8 w-8 shrink-0 sm:h-9 sm:w-9">
                    {(selectedConversation.metadata?.instagram_profile_pic || selectedConversation.metadata?.profile_picture) ? (
                      <AvatarImage src={selectedConversation.metadata.instagram_profile_pic || selectedConversation.metadata.profile_picture} alt={selectedConversation.lead_name} className="object-cover" />
                    ) : null}
                    <AvatarFallback className="bg-primary/90 text-sm font-medium text-primary-foreground">
                      {selectedConversation.lead_name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p
                      className="truncate text-sm font-medium leading-tight sm:text-[15px]"
                      title={selectedConversation.lead_name}
                    >
                      {selectedConversation.lead_name}
                    </p>
                    <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0 text-[11px] leading-tight text-muted-foreground">
                      <ConversationAttendingNotice
                        workspaceId={workspaceId}
                        assignedMemberId={selectedConversation.assigned_member_id}
                        assignedMemberIds={selectedConversation.assigned_member_ids}
                        conversationState={selectedConversation.state}
                      />
                      {selectedConversation.metadata?.instagram_username && (
                        <span className="truncate">@{selectedConversation.metadata.instagram_username}</span>
                      )}
                      {selectedConversation.channel === "whatsapp" && workspaceId ? (
                        <ConversationPhoneSwitch
                          workspaceId={workspaceId}
                          leadId={selectedConversation.lead_id}
                          leadName={selectedConversation.lead_name}
                          integrationId={selectedConversation.integration_id ?? null}
                          channel={selectedConversation.channel}
                          currentConversationId={selectedConversation.conversation_id}
                          currentNumber={selectedConversation.lead_phone}
                          onSwitched={handleSwitchLeadPhone}
                        />
                      ) : (
                        selectedConversation.lead_phone && !selectedConversation.metadata?.instagram_username && (
                          <span className="truncate tabular-nums">{selectedConversation.lead_phone}</span>
                        )
                      )}
                      {selectedConversation.originLabel && (
                        <span className="truncate">Via: {selectedConversation.originLabel}</span>
                      )}
                    </div>
                  </div>
                </> : <>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-muted to-muted/50">
                    <MessageSquare className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <span className="text-muted-foreground">Selecione uma conversa</span>
                </>}
              </div>

              {selectedConversation && (
                <div
                  ref={chatHeaderActionsRef}
                  className="flex shrink-0 items-center gap-0.5 sm:gap-1"
                >
                  <ConversationChatHeaderActions
                    conversation={selectedConversation}
                    workspaceId={workspaceId || ""}
                    memberId={memberId}
                    wide={useWideChatHeader}
                    leadActionsExpanded={leadActionsExpanded}
                    onLeadActionsExpandedChange={setLeadActionsExpanded}
                    isSummarizing={isSummarizing}
                    isLeadRecovering={isLeadRecovering}
                    leadRecoveryPlanAllowed={plusPlanAllowed}
                    agentResumeBlocked={leadBlocksAgentResume(selectedConversation)}
                    onAssign={handleLeadMemberAssign}
                    onPause={handlePauseConversation}
                    onResume={handleResumeConversation}
                    onMaybeClose={handleMaybeClose}
                    onReopen={handleReopenConversation}
                    onOpenLeadDetails={handleOpenLeadDetails}
                    onSummarize={handleSummarizeConversation}
                    onLeadRecovery={handleLeadRecovery}
                    onToggleFollowup={handleToggleFollowup}
                    onClearContext={handleClearContext}
                    onDelete={handleDeleteConversation}
                    onAgentResumeBlocked={() => toast(AGENT_BLOCKED_RESUME_TOAST)}
                    resolveEscalationButton={
                      <ResolveEscalationButton
                        leadId={selectedConversation.lead_id}
                        conversationId={selectedConversation.conversation_id}
                        workspaceId={workspaceId}
                        memberId={memberId}
                        integrationId={selectedConversation.integration_id}
                        onResolved={handleEscalationResolved}
                      />
                    }
                    resolveEscalationButtonCompact={
                      <ResolveEscalationButton
                        leadId={selectedConversation.lead_id}
                        conversationId={selectedConversation.conversation_id}
                        workspaceId={workspaceId}
                        memberId={memberId}
                        integrationId={selectedConversation.integration_id}
                        onResolved={handleEscalationResolved}
                      />
                    }
                  />
                </div>
              )}
            </div>

            {selectedConversation && !useWideChatHeader && leadActionsExpanded && (
              <ConversationChatHeaderExpandPanel
                conversation={selectedConversation}
                workspaceId={workspaceId || ""}
                memberId={memberId}
                onAssign={handleLeadMemberAssign}
                onMaybeClose={handleMaybeClose}
                onReopen={handleReopenConversation}
                resolveEscalationButton={
                  <ResolveEscalationButton
                    leadId={selectedConversation.lead_id}
                    conversationId={selectedConversation.conversation_id}
                    workspaceId={workspaceId}
                    memberId={memberId}
                    integrationId={selectedConversation.integration_id}
                    onResolved={handleEscalationResolved}
                    showLabel
                  />
                }
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden min-h-0 flex flex-col p-1">
          {selectedConversation ? <ChatInterface workspaceId={workspaceId} leadId={selectedConversation.lead_id} leadName={selectedConversation.lead_name} leadMetadata={selectedConversation.metadata} conversations={[{
            channel: selectedConversation.channel,
            conversation_id: selectedConversation.conversation_id,
            state: selectedConversation.state,
            followup_enabled: selectedConversation.followup_enabled,
            integration_id: selectedConversation.integration_id ?? null,
            originLabel: selectedConversation.originLabel ?? null,
          }]} onPauseConversation={handlePauseConversation} onResumeConversation={handleResumeConversation} onCloseConversation={handleMaybeClose} onToggleFollowup={handleToggleFollowup} onClearContext={handleClearContext} clearMessagesSignal={clearMessagesSignal} insertComposerText={insertComposerText} onLeadRecovery={handleLeadRecovery} isLeadRecovering={isLeadRecovering} leadRecoveryPlanAllowed={plusPlanAllowed} /> : <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
              <MessageSquare className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Nenhuma conversa selecionada</h3>
            <p className="text-muted-foreground text-sm max-w-sm">
              Selecione uma conversa da lista para começar a interagir
            </p>
          </div>}
        </CardContent>
      </Card>
    </div>

    {/* Diálogo de confirmação para limpar contexto */}
    <AlertDialog open={clearContextDialog.open} onOpenChange={open => !open && setClearContextDialog({
      open: false,
      conversationId: null
    })}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader className="space-y-4">
          <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500" />
            </div>
            Limpar Contexto
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Esta ação irá remover permanentemente:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground/70">•</span>
                  Todas as mensagens desta conversa
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground/70">•</span>
                  Todo o contexto e memória do agente
                </li>
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel className="border-0 bg-transparent hover:bg-muted">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={confirmClearContext} className="bg-destructive hover:bg-destructive/90">
            <Trash2 className="w-4 h-4 mr-2" />
            Limpar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Diálogo de confirmação para deletar lead */}
    <AlertDialog open={deleteLeadDialog.open} onOpenChange={open => !open && setDeleteLeadDialog({
      open: false,
      leadId: null,
      leadName: null
    })}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader className="space-y-4">
          <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500" />
            </div>
            Confirmar Exclusão
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p className="text-sm">
                <span className="text-muted-foreground">Excluir: </span>
                <span className="font-semibold text-primary">{deleteLeadDialog.leadName || 'Lead'}</span>
              </p>
               <p className="text-sm text-muted-foreground">
                 Esta ação irá remover permanentemente:
               </p>
               <ul className="space-y-2 text-sm text-muted-foreground">
                 <li className="flex items-start gap-2">
                   <span className="text-muted-foreground/70">•</span>
                   Esta conversa e todas as suas mensagens
                 </li>
               </ul>
               <p className="text-sm text-muted-foreground">
                 O lead será mantido e poderá ser acessado na página de Leads.
               </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel className="border-0 bg-transparent hover:bg-muted">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDeleteLead} className="bg-destructive hover:bg-destructive/90">
            <Trash2 className="w-4 h-4 mr-2" />
            Excluir
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Lead Detail Drawer */}
    <LeadDetailDrawer
      open={leadDrawerOpen}
      onOpenChange={setLeadDrawerOpen}
      lead={fullLead}
      workspaceId={workspaceId}
      onUpdate={() => setConversationsRefreshKey((prev) => prev + 1)}
      onEscalationResolved={handleEscalationResolved}
      conversationId={selectedConversation?.conversation_id ?? null}
      memberId={memberId}
    />

    {/* New Conversation Dialog */}
    {workspaceId && selectedChannel === 'whatsapp' && <NewConversationDialog open={newConversationOpen} onOpenChange={setNewConversationOpen} workspaceId={workspaceId} channel="whatsapp" integrationId={selectedIntegrationId} onConversationCreated={conv => {
      setSelectedConversation(conv);
      setConversationsRefreshKey(prev => prev + 1);
    }} />}

    <IntegrationChoiceDialog
      open={newLeadIntegrationChoiceOpen}
      onOpenChange={(open) => {
        setNewLeadIntegrationChoiceOpen(open);
        if (!open) setPendingNewLeadId(null);
      }}
      integrations={newLeadIntegrationChoices}
      loading={isStartingNewLeadConversation}
      onConfirm={async (integrationId) => {
        if (!pendingNewLeadId) return;
        setIsStartingNewLeadConversation(true);
        try {
          await startNewLeadConversation(pendingNewLeadId, integrationId);
        } finally {
          setIsStartingNewLeadConversation(false);
        }
      }}
    />

    {/* Conversation Summary Sheet */}
    <ConversationSummarySheet open={showSummarySheet} onOpenChange={setShowSummarySheet} data={summaryData} isLoading={isSummarizing} />
    <LeadRecoverySheet
      open={showLeadRecoverySheet}
      onOpenChange={setShowLeadRecoverySheet}
      data={leadRecoveryData}
      isLoading={isLeadRecovering}
      onInsertToChat={handleInsertRecoveryMessage}
    />

    {/* Schedule Close Dialog (WhatsApp only) */}
    {selectedConversation && (
      <ScheduleCloseDialog
        open={scheduleCloseOpen}
        onOpenChange={setScheduleCloseOpen}
        conversationId={selectedConversation.conversation_id}
        leadName={selectedConversation.lead_name}
        workspaceId={workspaceId || ""}
        onJustClose={() => handleCloseConversation(selectedConversation.conversation_id)}
        onJustClosePreserveAssignees={() =>
          handleCloseConversation(selectedConversation.conversation_id, {
            preserveAssignees: true,
          })
        }
        onScheduledClose={(didClose) => {
          if (didClose) {
            syncConversationClosedInInbox(selectedConversation.conversation_id);
          }
        }}
      />
    )}

    {bulkActionsBar}
  </div>;
}